import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_SHORT_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';
import { NEWSLETTER_CONSENT_KEY, isNewsletterConsentGiven } from '../../util/userHelpers';
// Import shared components
import {
  FieldCheckbox,
  FieldCheckboxGroup,
  FieldSelect,
  FieldTextInput,
  FieldBoolean,
  ValidationError,
} from '../../components';
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';

// Listing fields with schemaType 'long' whose key starts with "budget" represent a
// monetary amount (e.g. tasks budgetMin/budgetMax). They are stored as plain integers
// in publicData (no Money conversion); this regexp only selects the inline-currency display.
const BUDGET_KEY_REGEXP = /^budget/i;

// The direct-contact-service "Цена услуги от" field (key 'minimalPrice', schemaType 'shortText')
// is text-capable: it accepts either a number (e.g. "458") or free text (e.g. "по договорённости").
// It is always stored as a raw string in publicData; the euro symbol is only appended for display
// (on blur) when the value is digits-only — it is never persisted (never "458 €").
const MINIMAL_PRICE_KEY = 'minimalPrice';
const DIGITS_ONLY_REGEXP = /^\d+$/;

const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });
  const filterOptions = createFilterOptions(enumOptions);

  const label = getLabel(fieldConfig);

  return filterOptions ? (
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={label}
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {filterOptions.map(optionConfig => {
        const key = optionConfig.key;
        return (
          <option key={key} value={key}>
            {optionConfig.label}
          </option>
        );
      })}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
    : {};

  return enumOptions ? (
    <FieldCheckboxGroup
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      options={createFilterOptions(enumOptions)}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="textarea"
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldShortText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

// Text-capable price field for direct-contact-service ('minimalPrice', schemaType 'shortText').
// It keeps the exact storage convention of CustomFieldShortText (raw string in publicData, required
// validation only) but, like the price field, displays the currency inline: while editing the raw
// value is shown (e.g. "458" or "по договорённости"); on blur the euro symbol is appended ONLY when
// the value is digits-only (e.g. "458 €"). Text values are never altered and the stored value is
// always the raw string — never "458 €", never an integer, never a Money instance.
const CustomFieldMinimalPrice = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderEuroAmount' });
  const validateMaybe = isRequired ? required(requiredMessage || defaultRequiredMessage) : undefined;
  const id = formId ? `${formId}.${name}` : name;
  const [focused, setFocused] = React.useState(false);

  return (
    <Field name={name} validate={validateMaybe}>
      {({ input, meta }) => {
        const { invalid, touched, error } = meta;
        const hasError = !!(touched && invalid && error);
        const raw = input.value || '';
        const trimmed = raw.trim();
        const isDigitsOnly = trimmed !== '' && DIGITS_ONLY_REGEXP.test(trimmed);
        // Raw value while editing or when it contains text; "<digits> €" once blurred & digits-only.
        const displayValue = focused || !isDigitsOnly ? raw : `${trimmed} \u20AC`;
        const inputClasses = classNames({ [css.inputError]: hasError });
        // A5-01 (Q2): while the error text is shown, the input is marked invalid and refers to it
        const showsError = !!(touched && error);
        const errorId = `${id}-error`;

        return (
          <div className={css.customField}>
            {label ? <label htmlFor={id}>{label}</label> : null}
            <input
              id={id}
              name={input.name}
              type="text"
              autoComplete="off"
              placeholder={placeholder}
              className={inputClasses}
              value={displayValue}
              onChange={e => input.onChange(e.target.value)}
              onFocus={e => {
                setFocused(true);
                input.onFocus(e);
              }}
              onBlur={e => {
                setFocused(false);
                input.onBlur(e);
              }}
              aria-invalid={showsError ? true : undefined}
              aria-describedby={showsError ? errorId : undefined}
            />
            <ValidationError id={errorId} fieldMeta={meta} />
          </div>
        );
      }}
    </Field>
  );
};

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      parse={value => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
      onWheel={e => {
        // fix: number input should not change value on scroll
        if (e.target === document.activeElement) {
          // Prevent the input value change, because we prefer page scrolling
          e.target.blur();

          // Refocus immediately, on the next tick (after the current function is done)
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

// Budget fields (schemaType 'long' with a key like budgetMin/budgetMax) keep the exact integer
// behavior of CustomFieldLong (validateInteger / required, integer storage in publicData) but
// display the currency inline, like the price field: while editing the raw integer is shown
// (e.g. "458"); on blur the euro symbol is appended (e.g. "458 €"). A 'text' input is used (with
// inputMode 'numeric') so there are no native number spinners and so the trailing " €" can be
// rendered. Non-digit characters are stripped on change, so the stored value is always a plain
// integer (or null) — never a string like "458 €" and never a Money instance.
const CustomFieldBudgetMoney = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  const id = formId ? `${formId}.${name}` : name;
  const [focused, setFocused] = React.useState(false);

  return (
    <Field name={name} validate={value => validate(value, minimum, maximum)}>
      {({ input, meta }) => {
        const { invalid, touched, error } = meta;
        const hasError = !!(touched && invalid && error);
        const hasValue = input.value != null && input.value !== '';
        // Raw integer while editing, "<integer> €" once blurred and non-empty.
        const displayValue = !hasValue ? '' : focused ? `${input.value}` : `${input.value} \u20AC`;
        const inputClasses = classNames({ [css.inputError]: hasError });
        // A5-01 (Q2): while the error text is shown, the input is marked invalid and refers to it
        const showsError = !!(touched && error);
        const errorId = `${id}-error`;

        const handleChange = e => {
          const digits = e.target.value.replace(/[^\d]/g, '');
          const parsed = digits === '' ? null : Number.parseInt(digits, 10);
          input.onChange(Number.isNaN(parsed) ? null : parsed);
        };

        return (
          <div className={css.customField}>
            {label ? <label htmlFor={id}>{label}</label> : null}
            <input
              id={id}
              name={input.name}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder={placeholder}
              className={inputClasses}
              value={displayValue}
              onChange={handleChange}
              onFocus={e => {
                setFocused(true);
                input.onFocus(e);
              }}
              onBlur={e => {
                setFocused(false);
                input.onBlur(e);
              }}
              aria-invalid={showsError ? true : undefined}
              aria-describedby={showsError ? errorId : undefined}
            />
            <ValidationError id={errorId} fieldMeta={meta} />
          </div>
        );
      }}
    </Field>
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

// Marketing newsletter opt-in ('newsletterConsent', Console enum 'yes' / 'no' in privateData) is
// rendered as a single checkbox next to the field's label, instead of a 'yes' / 'no' select.
// The form value stays the stored string: the box is checked only for 'yes' (never for 'no' or a
// missing value) and toggling writes 'yes' / 'no'. It is never required, even if the hosted config
// marks it mandatory: marketing consent must not block signup.
const CustomFieldNewsletterConsent = props => {
  const { name, fieldConfig, formId } = props;
  const label = getLabel(fieldConfig);

  return (
    <FieldCheckbox
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      format={isNewsletterConsentGiven}
      parse={checked => (checked ? 'yes' : 'no')}
    />
  );
};

const CustomFieldYoutube = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType, key } = props?.fieldConfig || {};
  const isBudgetMoneyField = schemaType === SCHEMA_TYPE_LONG && BUDGET_KEY_REGEXP.test(key || '');
  const isMinimalPriceField =
    (schemaType === SCHEMA_TYPE_SHORT_TEXT || schemaType === SCHEMA_TYPE_TEXT) &&
    key === MINIMAL_PRICE_KEY;
  const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;

  return key === NEWSLETTER_CONSENT_KEY
    ? renderFieldComponent(CustomFieldNewsletterConsent, props)
    : schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldMultiEnum, props)
    : isMinimalPriceField
    ? renderFieldComponent(CustomFieldMinimalPrice, props)
    : schemaType === SCHEMA_TYPE_TEXT
    ? renderFieldComponent(CustomFieldText, props)
    : schemaType === SCHEMA_TYPE_SHORT_TEXT
    ? renderFieldComponent(CustomFieldShortText, props)
    : isBudgetMoneyField
    ? renderFieldComponent(CustomFieldBudgetMoney, props)
    : schemaType === SCHEMA_TYPE_LONG
    ? renderFieldComponent(CustomFieldLong, props)
    : schemaType === SCHEMA_TYPE_BOOLEAN
    ? renderFieldComponent(CustomFieldBoolean, props)
    : schemaType === SCHEMA_TYPE_YOUTUBE
    ? renderFieldComponent(CustomFieldYoutube, props)
    : null;
};

export default CustomExtendedDataField;
